import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiDownload, runAiJob } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  PenNib,
  Camera, CheckCircle, CloudSun, HardHat, MapPin, Ruler, Sparkle, Trash,
  CaretLeft, FilePdf, Plus,
} from "@phosphor-icons/react";
import FirmeDialog from "../components/FirmeDialog";

const RUOLI_VERBALE = [
  { key: "firma_tecnico", ruolo: "tecnico", label: "Il Tecnico incaricato", titolo: "Firma del Tecnico" },
  { key: "firma_impresa", ruolo: "impresa", label: "L'Impresa esecutrice", titolo: "Firma dell'Impresa" },
  { key: "firma_committente", ruolo: "committente", label: "Il Committente", titolo: "Firma del Committente" },
];

const oggi = () => new Date().toISOString().slice(0, 10);

export default function VerbaleRapido() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [cantieri, setCantieri] = useState(null);
  const [praticaId, setPraticaId] = useState(params.get("pratica") || "");
  const [obbligo, setObbligo] = useState(null);
  const [oggetto, setOggetto] = useState("");
  const [data, setData] = useState(oggi());
  const [note, setNote] = useState("");
  const [meteo, setMeteo] = useState("");
  const [foto, setFoto] = useState([]);
  const [salvando, setSalvando] = useState("");
  const [migliorando, setMigliorando] = useState(false);
  const [fatto, setFatto] = useState(null);

  useEffect(() => {
    api.get("/verbale-rapido/cantieri").then((r) => setCantieri(r.data.items)).catch(() => setCantieri([]));
  }, []);

  const cantiere = useMemo(
    () => (cantieri || []).find((c) => c.pratica_id === praticaId) || null,
    [cantieri, praticaId]
  );

  const scegliObbligo = (o) => {
    setObbligo(o);
    setOggetto(o.nome);
  };

  const rilevaMeteo = () => {
    if (!navigator.geolocation) { toast.error("Posizione non disponibile"); return; }
    toast.loading("Rilevo posizione e meteo...", { id: "meteo" });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data: m } = await api.get("/meteo/attuale", {
            params: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          });
          setMeteo(m.text);
          toast.success(`Meteo: ${m.text}`, { id: "meteo" });
        } catch { toast.error("Meteo non disponibile", { id: "meteo" }); }
      },
      () => toast.error("Permesso posizione negato", { id: "meteo" }),
      { timeout: 8000 }
    );
  };

  const aggiungiFoto = (e) => {
    const nuove = Array.from(e.target.files || []);
    if (nuove.length) setFoto((f) => [...f, ...nuove]);
    e.target.value = "";
  };

  const migliora = async () => {
    if (!note.trim()) { toast.error("Scrivi prima due appunti"); return; }
    setMigliorando(true);
    try {
      const r = await runAiJob("verbale-formale", { pratica_id: praticaId, note, oggetto, data, meteo });
      setNote(r.testo);
      toast.success("Appunti trasformati in verbale");
    } catch (e) { toast.error(e.message || "Errore IA"); }
    finally { setMigliorando(false); }
  };

  const [firmeOpen, setFirmeOpen] = useState(false);

  const salva = async () => {
    if (!praticaId) { toast.error("Scegli il cantiere"); return; }
    if (!oggetto.trim()) { toast.error("Indica cosa hai verificato"); return; }
    setSalvando("Salvo il verbale...");
    try {
      const { data: ev } = await api.post(`/pratiche/${praticaId}/cantiere`, {
        tipo: "sopralluogo",
        data,
        oggetto: oggetto.trim(),
        dettagli: note.trim() || (obbligo ? `Sopralluogo ${obbligo.ruolo} — ${obbligo.riferimento}` : ""),
        meteo: meteo || undefined,
        sopralluogo_codice: obbligo?.codice,
      });
      let caricate = 0;
      for (let i = 0; i < foto.length; i++) {
        setSalvando(`Carico foto ${i + 1} di ${foto.length}...`);
        const fd = new FormData();
        fd.append("file", foto[i]);
        try {
          await api.post(`/pratiche/${praticaId}/cantiere/${ev.id}/foto`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          caricate++;
        } catch { toast.error(`Foto ${i + 1} non caricata`); }
      }
      setFatto({ ...ev, foto_caricate: caricate });
      toast.success("Verbale registrato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSalvando(""); }
  };

  const nuovo = () => {
    setFatto(null); setObbligo(null); setOggetto(""); setNote(""); setFoto([]); setData(oggi());
    api.get("/verbale-rapido/cantieri").then((r) => setCantieri(r.data.items)).catch(() => {});
  };

  /* ---------- conferma ---------- */
  if (fatto) {
    return (
      <div className="max-w-md mx-auto px-4 py-10" data-testid="verbale-rapido-fatto">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
          <CheckCircle size={44} weight="fill" className="text-emerald-600 mx-auto" />
          <div className="text-lg font-semibold text-slate-900 mt-3">Verbale registrato</div>
          <div className="text-sm text-slate-600 mt-1">
            {fatto.oggetto}
            <br />
            {new Date(fatto.data).toLocaleDateString("it-IT")}
            {fatto.foto_caricate > 0 && ` · ${fatto.foto_caricate} foto`}
          </div>
        </div>
        <div className="grid gap-2 mt-5">
          <Button
            className="h-12 bg-[#0B1F3A] hover:bg-[#12294A]"
            onClick={() => setFirmeOpen(true)}
            data-testid="verbale-firma-tablet"
          >
            <PenNib size={16} /> {fatto.firmato_tablet ? "Firme raccolte · modifica" : "Fai firmare sul tablet"}
          </Button>
          <Button
            variant="outline"
            className="h-12"
            onClick={() => apiDownload(`/pratiche/${praticaId}/cantiere/${fatto.id}/pdf`, `verbale_${fatto.data}.pdf`)}
            data-testid="verbale-pdf"
          >
            <FilePdf size={16} /> Scarica il verbale in PDF
          </Button>
          <Button className="h-12" onClick={nuovo} data-testid="verbale-nuovo">
            <Plus size={16} /> Registra un altro sopralluogo
          </Button>
          <Button variant="ghost" className="h-11" onClick={() => navigate(`/pratiche/${praticaId}`)}>
            Vai all&apos;incarico
          </Button>
        </div>
        <FirmeDialog
          open={firmeOpen}
          onOpenChange={setFirmeOpen}
          prefix="vr-"
          titolo="Firme del verbale di sopralluogo"
          descrizione="Fai firmare tecnico, impresa e committente sul tablet: il verbale viene confermato e le firme inserite nel PDF."
          ruoli={RUOLI_VERBALE}
          valori={fatto}
          onSave={async (payload) => {
            const { data: ev } = await api.put(`/pratiche/${praticaId}/cantiere/${fatto.id}/firme`, payload);
            setFatto((f) => ({ ...f, ...ev }));
          }}
        />
      </div>
    );
  }

  /* ---------- scelta cantiere ---------- */
  if (!praticaId) {
    return (
      <div className="max-w-md mx-auto px-4 py-6" data-testid="verbale-rapido">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Verbale rapido</div>
        <h1 className="text-2xl font-semibold text-slate-900 mt-1">Su quale cantiere sei?</h1>
        <p className="text-sm text-slate-500 mt-1 mb-5">Tocca il cantiere per registrare il sopralluogo.</p>
        {cantieri === null && <div className="text-sm text-slate-400">Carico i cantieri...</div>}
        {cantieri?.length === 0 && (
          <div className="text-sm text-slate-500 rounded-lg border border-slate-200 p-4">
            Nessun cantiere attivo con obblighi da registrare.
          </div>
        )}
        <div className="grid gap-2">
          {(cantieri || []).map((c) => (
            <button
              key={c.pratica_id}
              onClick={() => setPraticaId(c.pratica_id)}
              className="text-left rounded-xl border border-slate-200 bg-white p-4 active:scale-[0.99] hover:border-slate-900 transition-all"
              data-testid={`vr-cantiere-${c.pratica_id}`}
            >
              <div className="text-sm font-semibold text-slate-900 uppercase truncate">{c.cliente_nome || c.titolo}</div>
              <div className="text-[11px] text-slate-500 truncate mt-0.5 flex items-center gap-1">
                <MapPin size={11} /> {c.indirizzo || c.titolo}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">
                  {c.mancanti} da registrare
                </span>
                {c.ultimo_sopralluogo && (
                  <span className="text-[10px] text-slate-400">
                    ultimo {new Date(c.ultimo_sopralluogo).toLocaleDateString("it-IT")}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  /* ---------- form verbale ---------- */
  return (
    <div className="max-w-md mx-auto px-4 py-6 pb-28" data-testid="verbale-rapido-form">
      <button onClick={() => { setPraticaId(""); setObbligo(null); setOggetto(""); }} className="text-xs text-slate-500 inline-flex items-center gap-1 mb-3" data-testid="vr-indietro">
        <CaretLeft size={12} /> cambia cantiere
      </button>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Verbale rapido</div>
      <h1 className="text-xl font-semibold text-slate-900 uppercase truncate">{cantiere?.cliente_nome || cantiere?.titolo || "Cantiere"}</h1>
      <div className="text-[11px] text-slate-500 truncate">{cantiere?.indirizzo}</div>

      <div className="mt-5">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">1 · Cosa hai verificato</div>
        <div className="grid gap-1.5">
          {(cantiere?.obblighi_da_fare || []).map((o) => (
            <button
              key={o.codice}
              onClick={() => scegliObbligo(o)}
              className={`text-left rounded-lg border p-3 transition-all ${obbligo?.codice === o.codice ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
              data-testid={`vr-obbligo-${o.codice}`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${obbligo?.codice === o.codice ? "border-white/30 text-white" : o.ruolo === "DL" ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-orange-100 text-orange-800 border-orange-200"}`}>
                  {o.ruolo === "DL" ? <Ruler size={9} className="inline mr-0.5" /> : <HardHat size={9} className="inline mr-0.5" />}
                  {o.ruolo}
                </span>
                <span className="text-[13px] font-medium leading-tight">{o.nome}</span>
              </div>
            </button>
          ))}
        </div>
        <Input
          value={oggetto}
          onChange={(e) => { setOggetto(e.target.value); setObbligo(null); }}
          placeholder="…oppure scrivi un altro oggetto"
          className="mt-2 h-11"
          data-testid="vr-oggetto"
        />
      </div>

      <div className="mt-5">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">2 · Foto</div>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={aggiungiFoto} data-testid="vr-file-input" />
        <button
          onClick={() => fileRef.current?.click()}
          className="w-full rounded-xl border-2 border-dashed border-slate-300 py-6 flex flex-col items-center gap-1 text-slate-600 active:bg-slate-50"
          data-testid="vr-scatta-foto"
        >
          <Camera size={26} />
          <span className="text-sm font-medium">Scatta o scegli foto</span>
          <span className="text-[11px] text-slate-400">anche più di una</span>
        </button>
        {foto.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-2" data-testid="vr-foto-preview">
            {foto.map((f, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                <img src={URL.createObjectURL(f)} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => setFoto((old) => old.filter((_, j) => j !== i))}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 flex items-center justify-center text-red-600"
                  data-testid={`vr-foto-del-${i}`}
                >
                  <Trash size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">3 · Note dal campo</div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={5}
          placeholder="Appunti veloci: cosa hai visto, chi era presente, prescrizioni date…"
          data-testid="vr-note"
        />
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Button variant="outline" size="sm" className="h-9 text-[11px]" onClick={migliora} disabled={migliorando} data-testid="vr-migliora-ia">
            <Sparkle size={12} /> {migliorando ? "L'IA sta scrivendo..." : "Trasforma in verbale (IA)"}
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-[11px]" onClick={rilevaMeteo} data-testid="vr-meteo">
            <CloudSun size={12} /> {meteo || "Rileva meteo"}
          </Button>
          <Input type="date" value={data} onChange={(e) => setData(e.target.value)} className="h-9 w-[140px] text-[12px]" data-testid="vr-data" />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-3 bg-white/95 backdrop-blur border-t border-slate-200 md:static md:bg-transparent md:border-0 md:p-0 md:mt-6">
        <div className="max-w-md mx-auto">
          <Button className="w-full h-14 text-base" onClick={salva} disabled={!!salvando} data-testid="vr-salva">
            {salvando || `Salva verbale${foto.length ? ` con ${foto.length} foto` : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
